import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () => (
  window.matchMedia("(display-mode: standalone)").matches
  || window.navigator.standalone === true
);

export function PwaInstallPrompt() {
  const { user } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [eligible, setEligible] = useState(false);
  const [promptRecorded, setPromptRecorded] = useState(false);

  const dismissKey = user?.id ? `ask-jonny-pwa-install-dismissed-${user.id}` : "";

  useEffect(() => {
    if (!user || installed) {
      setEligible(false);
      return;
    }
    api
      .get("/me/preferences")
      .then(({ data }) => {
        if (data.pwa_install_prompt_installed) {
          // A browser can remove an installed PWA without changing this saved preference.
          // Only display mode can confirm that Ask Jonny is currently installed on this device.
          setEligible(false);
          return;
        }
        const lastShown = Date.parse(data.pwa_install_prompt_last_shown || "");
        const onceThisMonth = Number.isFinite(lastShown) && Date.now() - lastShown < MONTH_MS;
        setEligible(!onceThisMonth);
      })
      .catch(() => setEligible(false));
  }, [installed, user]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      api.put("/me/preferences", {
        pwa_install_prompt_last_shown: new Date().toISOString(),
        pwa_install_prompt_installed: true,
      }).catch(() => undefined);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!installPrompt || !eligible || promptRecorded || !user) {
      return;
    }
    const shownAt = new Date().toISOString();
    setPromptRecorded(true);
    api.put("/me/preferences", { pwa_install_prompt_last_shown: shownAt }).catch(() => undefined);
  }, [eligible, installPrompt, promptRecorded, user]);

  useEffect(() => {
    if (!user || !installed) {
      return;
    }
    api.put("/me/preferences", {
      pwa_install_prompt_last_shown: new Date().toISOString(),
      pwa_install_prompt_installed: true,
    }).catch(() => undefined);
  }, [installed, user]);

  const install = useCallback(async () => {
    if (!installPrompt) {
      return;
    }
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      toast.success("Ask Jonny is being added to your device.");
    }
    setInstallPrompt(null);
  }, [installPrompt]);

  useEffect(() => {
    const onInstallRequest = () => {
      if (installed) {
        toast.info("Ask Jonny is already installed on this device.");
        return;
      }
      if (!installPrompt) {
        toast.info("Install is not available in this browser yet.");
        return;
      }
      install();
    };
    window.addEventListener("ask-jonny-install-request", onInstallRequest);
    return () => {
      window.removeEventListener("ask-jonny-install-request", onInstallRequest);
    };
  }, [install, installPrompt, installed]);

  const dismiss = () => {
    if (dismissKey) {
      sessionStorage.setItem(dismissKey, "true");
    }
    setInstallPrompt(null);
  };

  if (
    !installPrompt
    || !eligible
    || installed
    || (dismissKey && sessionStorage.getItem(dismissKey) === "true")
  ) {
    return null;
  }

  return (
    <aside
      className="fixed bottom-5 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 items-center gap-3 border border-emerald-700/20 bg-emerald-950 px-4 py-3 text-emerald-50 shadow-2xl"
      data-testid="pwa-install-banner"
    >
      <Download className="h-5 w-5 shrink-0 text-emerald-300" />
      <p className="min-w-0 flex-1 text-sm">
        Install Ask Jonny for a focused, app-style workspace.
      </p>
      <Button
        size="sm"
        className="shrink-0 bg-emerald-300 text-emerald-950 hover:bg-emerald-200"
        onClick={install}
        data-testid="pwa-install-button"
      >
        Install
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 shrink-0 text-emerald-50 hover:bg-emerald-900 hover:text-emerald-50"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="pwa-install-dismiss-button"
      >
        <X className="h-4 w-4" />
      </Button>
    </aside>
  );
}